import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components/atoms/badge';
import { Button } from '@components/atoms/button';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { CheckMarkIcon, CloseCircleIcon } from '@components/icons';
import { AttentionIcon } from '@components/icons/attention-icon';
import { BadgeStatusEnum } from '@models/enums';
import type { ThemeProps } from '@models/types';
import type { HintsProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/hints/types';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import cn from 'classnames';
import Lottie from 'lottie-react';

import { VerificationStatusEnum } from '../types';
import { getHintsPhoto } from '../utils/get-hints-photo';

import animation from './loader.json';
import { HintsContainer } from './styles';

export const Hints: React.FC<HintsProps> = ({
    faceDetectResult,
    prepareSeconds,
    verificationStatus,
    tryLoadFacemesh,
    detectCount,
    minDetectCount,
}) => {
    const theme = useTheme<ThemeProps>();
    const { t } = useTranslation();

    const isCanShowHints = detectCount >= minDetectCount;

    return (
        <HintsContainer
            className={cn('hints', {
                'hints--error':
                    verificationStatus === VerificationStatusEnum.FACE_NOT_FOUND ||
                    verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR ||
                    verificationStatus === VerificationStatusEnum.POOR_LIGHTING,
            })}
        >
            {verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING && (
                <div className='loading-face-detector'>
                    <Spinner size={20} style={{ marginRight: 16 }} />
                    {t('docVerificationWidget.liveness.loadingFaceDetector')}
                </div>
            )}
            {verificationStatus === VerificationStatusEnum.PREPARING && (
                <Box sx={{ width: '100%' }}>
                    <Typography
                        variant='text-bold'
                        color='primary-a7'
                        sx={{
                            fontSize: '36px !important',
                            position: {
                                sm: 'relative',
                                xs: 'relative',
                            },
                            top: {
                                sm: '4px',
                                xs: '4px',
                            },
                        }}
                    >
                        {prepareSeconds}
                    </Typography>
                </Box>
            )}
            {(verificationStatus === VerificationStatusEnum.READY ||
                (verificationStatus === VerificationStatusEnum.FACE_FOUND && !isCanShowHints)) && (
                <p
                    className='photo-hints'
                    dangerouslySetInnerHTML={{
                        __html: t('docVerificationWidget.liveness.alignFace'),
                    }}
                />
            )}
            {verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR && (
                <Box display='flex' flexDirection='column' gap='16px'>
                    <Box display='flex' justifyContent='center' alignItems='center'>
                        <AttentionIcon />
                        {t('docVerificationWidget.liveness.errorLoadingFaceDetector')}
                    </Box>
                    <Button
                        variant='primary'
                        onClick={tryLoadFacemesh}
                        style={{ maxWidth: 100, margin: 'auto' }}
                    >
                        {t('common.tryAgain')}
                    </Button>
                </Box>
            )}
            {isCanShowHints && verificationStatus === VerificationStatusEnum.FACE_NOT_FOUND && (
                <Box display='flex'>
                    <AttentionIcon />
                    {t('docVerificationWidget.liveness.textMask')}
                </Box>
            )}

            {verificationStatus === VerificationStatusEnum.POOR_LIGHTING && (
                <Badge
                    leftAddon={<CloseCircleIcon />}
                    text={t('docVerificationWidget.liveness.lightError')}
                    status={BadgeStatusEnum.ERROR}
                    className='error-badge'
                />
            )}

            {verificationStatus === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS && (
                <Lottie animationData={animation} loop={true} className='photo-hints' />
            )}
            {verificationStatus === VerificationStatusEnum.SUCCESS && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        borderRadius: '26px',
                        minHeight: '40px',
                        mx: 'auto',
                        width: '100%',
                        maxWidth: '280px',
                        color: 'primaryA7',
                        bgcolor: 'secondaryA3',
                        position: 'relative',
                        bottom: '10px',
                        boxShadow: `0px 0px 1px 0px ${theme.palette.primaryA5} inset, 0px -6px 44px 0px ${theme.palette.secondaryA3}B2`,

                        '@media (max-width: 340px)': {
                            maxWidth: '260px',
                            '& span': {
                                fontSize: '15px',
                            },
                        },
                    }}
                >
                    <CheckMarkIcon />
                    <Typography tag='span' variant='big-text-bold' color='primary-a7'>
                        {t('docVerificationWidget.liveness.checkCompleted')}
                    </Typography>
                </Box>
            )}

            {isCanShowHints &&
                faceDetectResult &&
                verificationStatus !== VerificationStatusEnum.FACE_NOT_FOUND &&
                verificationStatus !== VerificationStatusEnum.POOR_LIGHTING &&
                verificationStatus !== VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS &&
                verificationStatus !== VerificationStatusEnum.SUCCESS && (
                    <p
                        className='photo-hints'
                        dangerouslySetInnerHTML={{
                            __html: t(`${getHintsPhoto(faceDetectResult)}`),
                        }}
                    />
                )}
        </HintsContainer>
    );
};
